import React, { useState, useRef, useEffect } from 'react';

const JobStatus = {
  ALL: 'ALL',
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
  NEEDS_ATTENTION: 'NEEDS_ATTENTION',
  READY_FOR_INSPECTION: 'READY_FOR_INSPECTION'
};

const JobStatusFilter = ({ selectedStatuses = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const statusOptions = [
    { value: JobStatus.PENDING, label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: JobStatus.IN_PROGRESS, label: 'In Progress', color: 'bg-orange-100 text-orange-800' },
    { value: JobStatus.COMPLETED, label: 'Completed', color: 'bg-green-100 text-green-800' },
    { value: JobStatus.READY_FOR_INSPECTION, label: 'Ready for Inspection', color: 'bg-blue-100 text-blue-800' },
    { value: JobStatus.CANCELLED, label: 'Cancelled', color: 'bg-red-100 text-red-800' },
    { value: JobStatus.FAILED, label: 'Failed', color: 'bg-red-200 text-red-900' },
    { value: JobStatus.NEEDS_ATTENTION, label: 'Needs Attention', color: 'bg-purple-100 text-purple-800' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStatusToggle = (status) => {
    const newSelectedStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter(s => s !== status)
      : [...selectedStatuses, status];
    
    onChange(newSelectedStatuses);
  };

  const handleClearAll = () => {
    onChange([]);
    setIsOpen(false);
  };

  const handleSelectAll = () => {
    onChange(statusOptions.map(option => option.value));
    setIsOpen(false);
  };

  const getDisplayText = () => {
    if (selectedStatuses.length === 0) {
      return 'All Statuses';
    } else if (selectedStatuses.length === 1) {
      const selectedOption = statusOptions.find(option => option.value === selectedStatuses[0]);
      return selectedOption ? selectedOption.label : selectedStatuses[0];
    } else {
      return `${selectedStatuses.length} statuses selected`;
    }
  };

  return (
    <div className="space-y-2" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700">
        Job Status
      </label>
      
      {/* Multi-select dropdown button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
      >
        <span className="block truncate text-sm">
          {getDisplayText()}
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3z" clipRule="evenodd" />
          </svg>
        </span>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {/* Control buttons */}
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-xs text-sky-600 hover:text-sky-700 font-medium"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs text-gray-600 hover:text-gray-700 font-medium"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Status options */}
          {statusOptions.map((option) => (
            <div key={option.value} className="px-3 py-2 hover:bg-gray-100">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes(option.value)}
                  onChange={() => handleStatusToggle(option.value)}
                  className="h-4 w-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                />
                <span className="ml-3 text-sm text-gray-900">{option.label}</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${option.color}`}>
                  {option.value}
                </span>
              </label>
            </div>
          ))}
        </div>
      )}
      
      {/* Selected Statuses Display */}
      {selectedStatuses.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedStatuses.map((status) => {
            const statusOption = statusOptions.find(option => option.value === status);
            return statusOption ? (
              <span 
                key={status} 
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusOption.color}`}
              >
                {statusOption.label}
                <button
                  type="button"
                  onClick={() => handleStatusToggle(status)}
                  className="ml-1 hover:text-gray-700"
                >
                  ×
                </button>
              </span>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
};

export { JobStatus };
export default JobStatusFilter; 